import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  getProjectionInputs,
  updateProjectionInputs,
  updateVehicle,
  type EnrichedHolding,
  type Property,
  type Vehicle,
} from '../api';

interface Props {
  accountId: string;
  vehicles: Vehicle[];
  holdings: EnrichedHolding[];
  properties: Property[];
  // Called after a per-vehicle contribution save so the parent can re-fetch
  // vehicles. Without it the saved value would round-trip on next reload only.
  onVehicleSaved?: () => void;
}

interface AccountInputs {
  realReturnPct: number;
  yearsToRetire: number;
  swrPct: number;
  includeDownsize: boolean;
  replacementHomePounds: number;
}

const SAVE_DEBOUNCE_MS = 600;

export function ProjectionSection({
  accountId,
  vehicles,
  holdings,
  properties,
  onVehicleSaved,
}: Props) {
  const startingByVehicle = useMemo(() => startingValues(vehicles, holdings), [vehicles, holdings]);

  // Account-level knobs live in their own server row. Initialised with the
  // same defaults the backend hands out for an empty account so the UI
  // renders sensibly during the brief load.
  const [account, setAccount] = useState<AccountInputs>({
    realReturnPct: 4.5,
    yearsToRetire: 7,
    swrPct: 3.5,
    includeDownsize: false,
    replacementHomePounds: 300_000,
  });
  const [accountLoaded, setAccountLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getProjectionInputs(accountId)
      .then((p) => {
        if (cancelled) return;
        setAccount({
          realReturnPct: p.real_return_pct,
          yearsToRetire: p.years_to_retire,
          swrPct: p.swr_pct,
          includeDownsize: p.include_downsize,
          replacementHomePounds: Math.round((p.replacement_home_pence ?? 0) / 100),
        });
        setAccountLoaded(true);
      })
      .catch(() => {
        // Keep defaults; mark loaded so the user can still edit + save.
        setAccountLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  // Per-vehicle planned contributions are sourced from each Vehicle row
  // (in pence). Local state mirrors the prop so edits feel instant; we
  // debounce-save to the server in the background.
  const [perVehicle, setPerVehicle] = useState<Record<string, number>>({});
  useEffect(() => {
    const next: Record<string, number> = {};
    for (const v of vehicles) {
      next[v.id] = Math.round((v.planned_annual_contribution_pence ?? 0) / 100);
    }
    setPerVehicle((prev) => {
      // Preserve any unsaved edits — only overwrite values that match the
      // last known server value or are absent locally. Avoids clobbering
      // the input while the user is typing.
      const merged = { ...next, ...prev };
      // But for vehicles that vanished, drop them.
      for (const id of Object.keys(merged)) {
        if (!(id in next)) delete merged[id];
      }
      return merged;
    });
  }, [vehicles]);

  const homeEquityNowPence = useMemo(() => totalEquityPence(properties), [properties]);
  const homeEquityAtRetirementPence = useMemo(
    () => projectedEquityPence(properties, account.yearsToRetire),
    [properties, account.yearsToRetire]
  );
  const downsizeNetPence = account.includeDownsize
    ? Math.max(0, homeEquityAtRetirementPence - Math.round(account.replacementHomePounds * 100))
    : 0;

  const projection = useMemo(
    () =>
      project(
        {
          realReturnPct: account.realReturnPct,
          yearsToRetire: account.yearsToRetire,
          perVehicle,
        },
        startingByVehicle,
        vehicles,
        downsizeNetPence
      ),
    [account, perVehicle, startingByVehicle, vehicles, downsizeNetPence]
  );

  const finalPoint = projection[projection.length - 1];
  const finalWealth = finalPoint?.total ?? 0;
  const sustainableIncome = (finalWealth * account.swrPct) / 100;

  // Debounced server saves. One timer per save target keeps the channels
  // independent so a vehicle edit doesn't reset the account-level timer.
  const accountSaveTimer = useRef<number | null>(null);
  const vehicleSaveTimers = useRef<Record<string, number>>({});

  function persistAccount(next: AccountInputs) {
    if (!accountLoaded) return; // don't save defaults before initial load lands
    if (accountSaveTimer.current != null) window.clearTimeout(accountSaveTimer.current);
    accountSaveTimer.current = window.setTimeout(() => {
      updateProjectionInputs(accountId, {
        real_return_pct: next.realReturnPct,
        years_to_retire: Math.round(next.yearsToRetire),
        swr_pct: next.swrPct,
        include_downsize: next.includeDownsize,
        replacement_home_pence: Math.round(next.replacementHomePounds * 100),
      }).catch((err) => {
        console.warn('updateProjectionInputs failed', err);
      });
    }, SAVE_DEBOUNCE_MS);
  }

  function updateAccount(next: Partial<AccountInputs>) {
    const merged = { ...account, ...next };
    setAccount(merged);
    persistAccount(merged);
  }

  function setVehicleContrib(v: Vehicle, amount: number) {
    setPerVehicle((prev) => ({ ...prev, [v.id]: amount }));
    if (vehicleSaveTimers.current[v.id] != null) {
      window.clearTimeout(vehicleSaveTimers.current[v.id]);
    }
    vehicleSaveTimers.current[v.id] = window.setTimeout(() => {
      updateVehicle(accountId, v.id, {
        name: v.name,
        owner: v.owner,
        kind: v.kind,
        cash_pence: v.cash_pence,
        platform_fee_pct_per_year: v.platform_fee_pct_per_year,
        platform_fee_flat_pence: v.platform_fee_flat_pence,
        advisor_fee_pct_per_year: v.advisor_fee_pct_per_year,
        planned_annual_contribution_pence: Math.round(amount * 100),
        contributions: v.contributions,
      })
        .then(() => onVehicleSaved?.())
        .catch((err) => console.warn('updateVehicle (planned contribution) failed', err));
    }, SAVE_DEBOUNCE_MS);
  }

  return (
    <div>
      <p style={{ marginTop: 0, color: '#475569', fontSize: '0.9rem' }}>
        Projects each vehicle forward at a real (after-inflation) return rate. Annual contributions
        compound on top. The SWR (safe withdrawal rate) is what the final pot sustainably yields per
        year for the rest of retirement.
      </p>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}
      >
        <NumField
          label="Real return %"
          value={account.realReturnPct}
          step={0.1}
          onChange={(v) => updateAccount({ realReturnPct: v })}
          hint="After inflation. Base case 4.5% for 60/40."
        />
        <NumField
          label="Years to retirement"
          value={account.yearsToRetire}
          step={1}
          min={1}
          max={50}
          onChange={(v) => updateAccount({ yearsToRetire: v })}
          hint="Andy 50 → 57 = 7 years."
        />
        <NumField
          label="SWR %"
          value={account.swrPct}
          step={0.1}
          onChange={(v) => updateAccount({ swrPct: v })}
          hint="3.5% for 38+ year horizon, 4% for 30."
        />
      </div>

      <div style={{ marginBottom: '1rem' }}>
        <h4 style={{ marginBottom: '0.5rem' }}>Annual contribution per vehicle (£)</h4>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '0.5rem',
          }}
        >
          {vehicles.map((v) => (
            <label
              key={v.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: '0.85rem',
              }}
            >
              <span style={{ flex: 1, color: '#475569' }}>
                {v.name}
                <span style={{ color: '#94a3b8' }}> ({v.owner})</span>
              </span>
              <input
                type="number"
                step="500"
                min="0"
                value={perVehicle[v.id] ?? 0}
                onChange={(e) => setVehicleContrib(v, Number(e.target.value) || 0)}
                style={{ width: '6.5rem' }}
              />
            </label>
          ))}
        </div>
        <div style={{ marginTop: '0.4rem', fontSize: '0.85rem', color: '#475569' }}>
          Total: {formatGBP(totalContribPence(perVehicle))} per year.
        </div>
      </div>

      <div
        style={{
          marginBottom: '1rem',
          padding: '0.75rem',
          border: '1px solid #e2e8f0',
          borderRadius: '0.5rem',
          background: account.includeDownsize ? '#f0fdf4' : '#f8fafc',
        }}
      >
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 500 }}>
          <input
            type="checkbox"
            checked={account.includeDownsize}
            onChange={(e) => updateAccount({ includeDownsize: e.target.checked })}
          />
          Include home-sale proceeds at retirement
        </label>
        {account.includeDownsize && (
          <div
            style={{
              marginTop: '0.6rem',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '0.75rem',
              fontSize: '0.85rem',
            }}
          >
            <div>
              <div style={{ color: '#475569' }}>Equity at retirement</div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600 }}>
                {formatGBP(homeEquityAtRetirementPence)}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                {properties.length === 0
                  ? 'Add a property in the Property tab to populate this.'
                  : homeEquityAtRetirementPence !== homeEquityNowPence
                    ? `Now ${formatGBP(homeEquityNowPence)} → +${formatGBP(homeEquityAtRetirementPence - homeEquityNowPence)} from mortgage paydown`
                    : `${formatGBP(homeEquityNowPence)} now (no rate/payment set, no paydown modelled)`}
              </div>
            </div>
            <NumField
              label="Replacement home cost £"
              value={account.replacementHomePounds}
              step={5_000}
              min={0}
              onChange={(v) => updateAccount({ replacementHomePounds: v })}
              hint="Cost of the smaller home you'd buy. Set 0 for full sale."
            />
            <div>
              <div style={{ color: '#475569' }}>Net proceeds at retirement</div>
              <div
                style={{
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  color: downsizeNetPence > 0 ? '#16a34a' : '#94a3b8',
                }}
              >
                {formatGBP(downsizeNetPence)}
              </div>
              <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>
                Injected as a one-off at year {account.yearsToRetire}.
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '0.75rem',
          marginBottom: '1rem',
        }}
      >
        <Stat
          label={`Wealth at year ${account.yearsToRetire}`}
          value={formatGBP(finalWealth)}
          tone="positive"
        />
        <Stat
          label={`Sustainable income (${account.swrPct}%)`}
          value={`${formatGBP(sustainableIncome)} / yr`}
          sub="Today's-money, indefinite"
        />
        <Stat label="Starting wealth" value={formatGBP(projection[0]?.total ?? 0)} />
        <Stat
          label="Total contributions"
          value={formatGBP(totalContribPence(perVehicle) * account.yearsToRetire)}
          sub={`Over ${account.yearsToRetire} years`}
        />
        {account.includeDownsize && (
          <Stat
            label="Home sale proceeds"
            value={formatGBP(downsizeNetPence)}
            sub={`At year ${account.yearsToRetire}`}
          />
        )}
      </div>

      <div style={{ width: '100%', height: 320 }}>
        <ResponsiveContainer>
          <AreaChart data={projection} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="year" tick={{ fontSize: 12 }} />
            <YAxis
              tick={{ fontSize: 12 }}
              tickFormatter={(v: number) => formatShortGBP(v)}
              width={70}
            />
            <Tooltip
              formatter={(v) => formatGBP(typeof v === 'number' ? v : Number(v) || 0)}
              labelFormatter={(y) => `Year ${y}`}
            />
            <Legend />
            <Area
              type="monotone"
              dataKey="contributions"
              stackId="1"
              stroke="#0284c7"
              fill="#bae6fd"
              name="Cumulative contributions"
            />
            <Area
              type="monotone"
              dataKey="growth"
              stackId="1"
              stroke="#16a34a"
              fill="#bbf7d0"
              name="Growth on top"
            />
            {account.includeDownsize && (
              <Area
                type="step"
                dataKey="homeProceeds"
                stackId="1"
                stroke="#a855f7"
                fill="#e9d5ff"
                name="Home sale proceeds"
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function startingValues(vehicles: Vehicle[], holdings: EnrichedHolding[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const v of vehicles) out[v.id] = v.cash_pence ?? 0;
  for (const h of holdings) {
    if (!h.vehicle_id) continue;
    out[h.vehicle_id] = (out[h.vehicle_id] ?? 0) + (h.market_value_pence ?? 0);
  }
  return out;
}

interface ProjectionPoint {
  year: number;
  contributions: number; // cumulative, pence
  growth: number; // pence
  homeProceeds: number; // pence — only non-zero at the retirement year if downsize on
  total: number; // pence
}

interface ProjectionParams {
  realReturnPct: number;
  yearsToRetire: number;
  perVehicle: Record<string, number>;
}

function project(
  params: ProjectionParams,
  startingPerVehicle: Record<string, number>,
  vehicles: Vehicle[],
  downsizeNetPence: number
): ProjectionPoint[] {
  const r = params.realReturnPct / 100;
  const startingTotalPence = vehicles.reduce((s, v) => s + (startingPerVehicle[v.id] ?? 0), 0);
  const annualContribPence = totalContribPence(params.perVehicle);

  const points: ProjectionPoint[] = [];
  let wealth = startingTotalPence;
  let cumulativeContrib = 0;
  points.push({
    year: 0,
    contributions: cumulativeContrib,
    growth: wealth - cumulativeContrib,
    homeProceeds: 0,
    total: wealth,
  });
  for (let y = 1; y <= params.yearsToRetire; y++) {
    // Apply growth on prior balance, then add contribution end-of-year.
    wealth = wealth * (1 + r) + annualContribPence;
    cumulativeContrib += annualContribPence;
    // One-off downsize injection at the retirement year only.
    const proceeds = y === params.yearsToRetire ? downsizeNetPence : 0;
    if (proceeds > 0) wealth += proceeds;
    const initialPlusContrib = startingTotalPence + cumulativeContrib;
    points.push({
      year: y,
      contributions: initialPlusContrib,
      growth: Math.max(0, wealth - initialPlusContrib - proceeds),
      homeProceeds: proceeds,
      total: wealth,
    });
  }
  return points;
}

function totalEquityPence(properties: Property[]): number {
  return properties.reduce(
    (s, p) => s + Math.max(0, p.current_value_pence - p.mortgage_balance_pence),
    0
  );
}

// projectedEquityPence amortizes each mortgage forward `years` years and
// returns total equity (sum of value − projected balance) at that point.
// Where rate + monthly payment aren't both set, the property's current
// balance is used as the projected balance (no paydown assumed). Property
// value is held constant — by user's call, no growth.
function projectedEquityPence(properties: Property[], years: number): number {
  const months = Math.max(0, Math.round(years * 12));
  let total = 0;
  for (const p of properties) {
    const projectedBalance = projectMortgageBalancePence(
      p.mortgage_balance_pence,
      p.mortgage_interest_rate_pct ?? 0,
      p.mortgage_monthly_payment_pence ?? 0,
      months
    );
    total += Math.max(0, p.current_value_pence - projectedBalance);
  }
  return total;
}

// projectMortgageBalancePence runs a standard amortization forward N
// months. If payment is 0 (interest-only proxy) or rate makes the loan
// non-amortizing, returns whatever balance remains — never goes
// negative. If payment + rate aren't both set, returns the input balance.
function projectMortgageBalancePence(
  balancePence: number,
  ratePct: number,
  monthlyPaymentPence: number,
  months: number
): number {
  if (balancePence <= 0) return 0;
  if (ratePct <= 0 || monthlyPaymentPence <= 0) {
    // Plain pay-down without interest, or no schedule given.
    if (monthlyPaymentPence > 0) {
      return Math.max(0, balancePence - monthlyPaymentPence * months);
    }
    return balancePence;
  }
  const monthlyRate = ratePct / 100 / 12;
  let balance = balancePence;
  for (let m = 0; m < months; m++) {
    const interest = balance * monthlyRate;
    const principal = monthlyPaymentPence - interest;
    if (principal <= 0) {
      // Payment doesn't cover interest — balance grows. Cap at current
      // balance to avoid runaway numbers in the projection.
      balance = balance + interest - monthlyPaymentPence;
      continue;
    }
    balance -= principal;
    if (balance <= 0) return 0;
  }
  return Math.round(balance);
}

function totalContribPence(perVehicle: Record<string, number>): number {
  return Object.values(perVehicle).reduce((s, v) => s + (v || 0) * 100, 0);
}

function NumField({
  label,
  value,
  step,
  min,
  max,
  hint,
  onChange,
}: {
  label: string;
  value: number;
  step?: number;
  min?: number;
  max?: number;
  hint?: string;
  onChange: (v: number) => void;
}) {
  return (
    <label style={{ display: 'block', fontSize: '0.85rem' }}>
      <span style={{ color: '#475569', fontWeight: 500 }}>{label}</span>
      <input
        type="number"
        step={step ?? 1}
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        style={{ display: 'block', width: '100%', marginTop: '0.2rem' }}
      />
      {hint && <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>{hint}</span>}
    </label>
  );
}

function Stat({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: 'positive';
}) {
  const colour = tone === 'positive' ? '#16a34a' : '#0f172a';
  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: '0.5rem', padding: '0.75rem' }}>
      <div style={{ fontSize: '0.8rem', color: '#64748b' }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: '1.25rem', color: colour }}>{value}</div>
      {sub && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{sub}</div>}
    </div>
  );
}

const gbp = new Intl.NumberFormat('en-GB', {
  style: 'currency',
  currency: 'GBP',
  maximumFractionDigits: 0,
});
function formatGBP(pence: number): string {
  return gbp.format(pence / 100);
}

function formatShortGBP(pence: number): string {
  const pounds = pence / 100;
  if (Math.abs(pounds) >= 1_000_000) return `£${(pounds / 1_000_000).toFixed(1)}M`;
  if (Math.abs(pounds) >= 1_000) return `£${(pounds / 1_000).toFixed(0)}k`;
  return `£${pounds.toFixed(0)}`;
}
